import { SideBar, Tag } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useState } from "react";
import { NavLink } from "react-router-dom";

import { isMswEnabled } from "@/mswHelpers";
import { DevPanel } from "@components/DevPanel/DevPanel";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { IS_TEST_ENV, LS_KEYS } from "@core/core.constants";
import {
  CircleQuestion,
  Folder,
  House01,
  LayerTriple,
  Logout,
  Search,
  Settings,
  TicketSimple,
  UsersGroup,
  WindowTerminal,
} from "@icons";
import { ROUTES } from "@router/router.constants";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { onLogout } from "@utils/auth.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./Menu.module.css";
import type { MenuProps } from "./Menu.types";
import { HelpModal } from "../HelpModal/HelpModal";
import { SearchDrawer } from "../SearchDrawer/SearchDrawer";

export const Menu: FC<MenuProps> = ({ isMobile, isOpen, onClose }) => {
  const { t } = useCustomTranslation("common");
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isCameleon = Boolean(localStorage.getItem(LS_KEYS.CAMELEON_EMAIL));
  const { hasTicketing } = useTenant();
  const { currentGroup } = useGroupReferences();
  const { data: isGroupAdmin } = useGetGroup({
    groupId: currentGroup,
    options: { select: (res) => res.isAdmin },
  });

  const handleItemClick = isMobile ? onClose : undefined;

  const menuContent = (
    <>
      <SideBar.Header className={classes.header}>
        <SideBar.Header.Logo
          hasPinButton
          icon="companyLogo"
          label={t("brandName")}
        />
      </SideBar.Header>
      <SideBar.Body>
        {isCameleon && (
          <div className={classes.cameleonContainer}>
            <img
              alt={t("sidebar.cameleon.alt")}
              className={classes.cameleonImage}
              src="/images/app-cameleon.gif"
            />
            <Tag
              label={localStorage.getItem(LS_KEYS.CAMELEON_FULL_NAME) || ""}
            />
          </div>
        )}
        <SideBar.Separator />
        <SideBar.Item
          data-testid="search"
          icon={Search}
          label={t("sidebar.quickSearch")}
          onClick={() => {
            handleItemClick?.();
            setIsDrawerOpen(true);
          }}
        />
        <SideBar.Item
          as={NavLink}
          data-testid="test-menu-dashboard"
          icon={House01}
          label={t("sidebar.dashboard")}
          onClick={handleItemClick}
          to={ROUTES.DASHBOARD}
        />
        {isGroupAdmin && (
          <SideBar.Item
            as={NavLink}
            data-testid="test-menu-access-manager"
            icon={UsersGroup}
            label={t("sidebar.coworkers")}
            onClick={handleItemClick}
            to={ROUTES.ACCESS_MANAGER}
          />
        )}
        <SideBar.Item
          as={NavLink}
          data-testid="test-menu-perimeter"
          icon={LayerTriple}
          label={t("sidebar.perimeter")}
          onClick={handleItemClick}
          to={ROUTES.PERIMETER_CUSTOMERS}
        />
        <SideBar.Item
          as={NavLink}
          data-testid="test-menu-documents"
          icon={Folder}
          label={t("sidebar.document")}
          onClick={handleItemClick}
          to={ROUTES.DOCUMENTS}
        />
        {hasTicketing && (
          <SideBar.Item
            as={NavLink}
            data-testid="test-menu-tickets"
            icon={TicketSimple}
            label={t("sidebar.techRequest")}
            onClick={handleItemClick}
            to={ROUTES.FRESHDESK_TICKETS}
          />
        )}
        <SideBar.Item
          as={NavLink}
          data-testid="test-menu-settings"
          icon={Settings}
          label={t("sidebar.settings")}
          onClick={handleItemClick}
          to={ROUTES.SETTINGS}
        />
        <SideBar.Item
          data-testid="test-menu-help"
          icon={CircleQuestion}
          label={t("sidebar.help")}
          onClick={() => {
            handleItemClick?.();
            setIsHelpModalOpen(true);
          }}
        />
      </SideBar.Body>
      <SideBar.Footer>
        {IS_TEST_ENV && (
          <SideBar.ExtendItem
            className={classNames(
              isMswEnabled() && classes.mswActive,
              isMswEnabled() && classes.isMswAnimated,
            )}
            icon={WindowTerminal}
            label="Dev Panel"
          >
            <DevPanel />
          </SideBar.ExtendItem>
        )}
        <SideBar.Item
          data-testid="test-menu-logout"
          icon={Logout}
          label={t("sidebar.logout")}
          onClick={onLogout}
        />
      </SideBar.Footer>
    </>
  );

  return (
    <>
      {isMobile ? (
        isOpen && (
          <SideBar className={classes.sidebar} isMobile onModalClose={onClose}>
            {menuContent}
          </SideBar>
        )
      ) : (
        <SideBar className={classes.sidebar} isPinned>
          {menuContent}
        </SideBar>
      )}
      {isHelpModalOpen && (
        <HelpModal onClose={() => setIsHelpModalOpen(false)} />
      )}
      {isDrawerOpen && <SearchDrawer onClose={() => setIsDrawerOpen(false)} />}
    </>
  );
};
